
/**
 * Module dependencies.
 */

import { ProductColor } from 'src/types/products';
import { availableFiltersKeys } from 'src/core/collections-config/filters';
import { colorValues, colors } from 'src/core/collections-config/colors';
import { useFiltersList } from 'src/context/filters-list/context';
import { useTranslate } from 'src/core/utils/translator';
import FilterDisclosure from 'src/components/filters/filter-disclosure';
import FilterItem from 'src/components/filters/filter-item';
import FilterItemLabel from 'src/components/filters/filter-item-label';
import React, { ReactElement, useMemo } from 'react';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import sortBy from 'lodash/sortBy';
import split from 'lodash/split';

/**
 * Filter name.
 */

const filterName = availableFiltersKeys.color;

/**
 * `Props` type.
 */

type Props = {
  filtersList?: ProductColor[]
};

/**
 * `ColorFilter` component.
 */

const ColorFilter = ({ filtersList }: Props): ReactElement => {
  const { translate } = useTranslate();
  const { filters } = useFiltersList();
  const hasFilter = keys(filters).includes(filterName);
  const selectedFilters = hasFilter ? split(filters[filterName], ',').map(colorName => {
    return translate(`collections:colors.${colorName}`);
  }) : [];

  const colorsList = useMemo(() => {
    const orderedColors = sortBy(colors, color => {
      return translate(`collections:colors.${color}`);
    });

    if (isEmpty(filtersList)) {
      return orderedColors;
    }

    return filter(orderedColors, color => includes(filtersList, color));
  }, [filtersList, translate]);

  return (
    <FilterDisclosure
      name={filterName}
      selectedFilters={selectedFilters}
      title={translate('common:labels.color')}
    >
      {colorsList.map((colorName: string) => (
        <FilterItem
          key={colorName}
          label={(
            <FilterItemLabel
              title={translate(`collections:colors.${colorName}`)}
              type={'color'}
              value={colorValues[colorName]}
            />
          )}
          name={filterName}
          value={colorName}
        />
      ))}
    </FilterDisclosure>
  );
};

/**
 * Export `ColorFilter` component.
 */

export default ColorFilter;
